import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ContactSection from "../components/ContactSection";

const categories = [
    'All Categories',
    'Enterprise Infrastructure & Architecture',
    'Enterprise Networking & Automation',
    'Cybersecurity & Secure Access',
    'Cybersecurity & Risk Management',
    'Data Center & Cloud Strategy',
    'Managed Services',
    'Collaboration & Unified Communications',
];

const blogs = [
    {
        id: 1,
        title: 'High-Level vs Low-Level Design in IT Infrastructure: Why Both Are Critical for Implementation Success',
        category: 'Enterprise Infrastructure & Architecture',
        date: 'Jan 10, 2025',
        image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&auto=format&fit=crop&q=80',
        link: '/blog/website-development',
    },
    {
        id: 2,
        title: 'Redefining Network Architecture with AI-Native Models: A Practical Enterprise Guide',
        category: 'Enterprise Networking & Automation',
        date: 'Jan 14, 2025',
        image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=80',
        link: '/blog/website-development',
    },
    {
        id: 3,
        title: 'Zero Trust Network Access vs Traditional VPN: Building Secure Enterprise Connectivity',
        category: 'Cybersecurity & Secure Access',
        date: 'Jan 18, 2025',
        image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&auto=format&fit=crop&q=80',
        link: '/blog/it-consulting',
    },
    {
        id: 4,
        title: 'The Future of Data Centers: Hyperconverged Infrastructure and Software-Defined Everything',
        category: 'Data Center & Cloud Strategy',
        date: 'Jan 22, 2025',
        image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&auto=format&fit=crop&q=80',
        link: '/blog/website-development',
    },
    {
        id: 5,
        title: 'How AI and Machine Learning Are Reshaping Cybersecurity Threat Detection in 2025',
        category: 'Cybersecurity & Risk Management',
        date: 'Jan 26, 2025',
        image: 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=800&auto=format&fit=crop&q=80',
        link: '/blog/it-consulting',
    },
    {
        id: 6,
        title: 'SD-WAN vs MPLS: Choosing the Right WAN Strategy for Distributed Enterprise Networks',
        category: 'Enterprise Networking & Automation',
        date: 'Feb 3, 2025',
        image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop&q=80',
        link: '/blog/it-consulting',
    },
    {
        id: 7,
        title: 'Building a Resilient IT Infrastructure: Disaster Recovery Planning for Modern Enterprises',
        category: 'Enterprise Infrastructure & Architecture',
        date: 'Feb 8, 2025',
        image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&auto=format&fit=crop&q=80',
        link: '/blog/website-development',
    },
    {
        id: 8,
        title: 'Managed SOC Services: Why In-House Security Operations Centres Are Becoming Obsolete',
        category: 'Managed Services',
        date: 'Feb 12, 2025',
        image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&auto=format&fit=crop&q=80',
        link: '/blog/it-consulting',
    },
    {
        id: 9,
        title: 'Cisco Unified Communications: Transforming Enterprise Collaboration at Scale',
        category: 'Collaboration & Unified Communications',
        date: 'Feb 17, 2025',
        image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&auto=format&fit=crop&q=80',
        link: '/blog/digital-marketing',
    },
];

export default function Blogs() {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    const [selectedCategory, setSelectedCategory] = useState('All Categories');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const filtered = selectedCategory === 'All Categories'
        ? blogs
        : blogs.filter(b => b.category === selectedCategory);

    return (
        <>
            {/* ── HERO ── */}
            <section style={{
                position: 'relative', width: '100%', minHeight: '360px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover', backgroundPosition: 'center', overflow: 'hidden',
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,16,40,0.80)' }} />
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(37,99,235,0.06) 1px,transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none',
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '130px 15px 90px', textAlign: 'center' }}>
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.2)', border: '1px solid rgba(37,99,235,0.4)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '20px',
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Insights & Resources
                            </span>
                        </div>
                        <h1 style={{
                            color: '#fff', fontWeight: 800,
                            fontSize: 'clamp(2.4rem, 5vw, 4rem)',
                            lineHeight: 1.15, margin: '0 auto 18px', maxWidth: '700px',
                        }}>
                            Blogs & Insights
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem',
                            lineHeight: 1.8, maxWidth: '500px', margin: '0 auto',
                        }}>
                            Expert perspectives on enterprise IT, cybersecurity, cloud infrastructure, and digital transformation.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ── BLOG GRID ── */}
            <section style={{ padding: '80px 0 100px', background: '#fff' }}>
                <div className="container">

                    {/* Heading row + category dropdown */}
                    <div style={{
                        display: 'flex', alignItems: 'flex-end',
                        justifyContent: 'space-between', flexWrap: 'wrap',
                        gap: '20px', marginBottom: '52px',
                    }}>
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }} viewport={{ once: true }}
                            style={{ color: '#0f172a', fontWeight: 800, fontSize: 'clamp(2rem, 4vw, 3rem)', margin: 0, lineHeight: 1.1 }}
                        >
                            All <span style={{ color: '#2563eb' }}>Blogs</span>
                        </motion.h2>

                        {/* Category dropdown */}
                        <div ref={dropdownRef} style={{ position: 'relative', minWidth: '220px' }}>
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                style={{
                                    width: '100%', display: 'flex', alignItems: 'center',
                                    justifyContent: 'space-between', gap: '12px',
                                    padding: '11px 18px', borderRadius: '50px',
                                    border: '1.5px solid #e2e8f0', background: '#fff',
                                    color: '#0f172a', fontWeight: 600, fontSize: '0.9rem',
                                    cursor: 'pointer', transition: 'border-color 0.2s',
                                    fontFamily: 'inherit',
                                }}
                                onMouseOver={e => e.currentTarget.style.borderColor = '#2563eb'}
                                onMouseOut={e => { if (!dropdownOpen) e.currentTarget.style.borderColor = '#e2e8f0'; }}
                            >
                                <span>{selectedCategory}</span>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                    style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                                    <path d="M6 9l6 6 6-6" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>

                            {dropdownOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.15 }}
                                    style={{
                                        position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                                        background: '#fff', borderRadius: '14px',
                                        border: '1.5px solid #e2e8f0',
                                        boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                                        zIndex: 100, overflow: 'hidden',
                                    }}
                                >
                                    {categories.map((cat, i) => (
                                        <button key={i} onClick={() => { setSelectedCategory(cat); setDropdownOpen(false); }}
                                            style={{
                                                display: 'block', width: '100%', textAlign: 'left',
                                                padding: '11px 18px', border: 'none',
                                                borderBottom: i < categories.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                background: selectedCategory === cat ? '#eff6ff' : '#fff',
                                                color: selectedCategory === cat ? '#2563eb' : '#334155',
                                                fontWeight: selectedCategory === cat ? 700 : 400,
                                                fontSize: '0.88rem', cursor: 'pointer',
                                                transition: 'background 0.15s',
                                                fontFamily: 'inherit',
                                            }}
                                            onMouseOver={e => { if (selectedCategory !== cat) e.currentTarget.style.background = '#f8fafc'; }}
                                            onMouseOut={e => { if (selectedCategory !== cat) e.currentTarget.style.background = '#fff'; }}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </div>
                    </div>

                    {/* Blog cards grid */}
                    {filtered.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: '1rem' }}>
                            No blogs found in this category.
                        </div>
                    ) : (
                        <div className="row g-4">
                            {filtered.map((blog, i) => (
                                <div key={blog.id} className="col-lg-4 col-md-6">
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.45, delay: i * 0.06 }}
                                        viewport={{ once: true }}
                                    >
                                        <Link to={blog.link} style={{ textDecoration: 'none', display: 'block' }}>
                                            {/* Image */}
                                            <div style={{
                                                borderRadius: '16px', overflow: 'hidden',
                                                marginBottom: '20px', aspectRatio: '16/10',
                                                transition: 'transform 0.3s',
                                            }}
                                                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'}
                                                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                                            >
                                                <img src={blog.image} alt={blog.title}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                            </div>

                                            {/* Category tag */}
                                            <span style={{
                                                fontSize: '0.74rem', fontWeight: 700,
                                                color: '#2563eb', letterSpacing: '0.5px',
                                                textTransform: 'uppercase', display: 'block',
                                                marginBottom: '10px',
                                            }}>
                                                {blog.category}
                                            </span>

                                            {/* Title */}
                                            <h3 style={{
                                                color: '#0f172a', fontWeight: 700,
                                                fontSize: '1.02rem', lineHeight: 1.55,
                                                marginBottom: '16px',
                                                transition: 'color 0.2s',
                                            }}
                                                onMouseOver={e => e.currentTarget.style.color = '#2563eb'}
                                                onMouseOut={e => e.currentTarget.style.color = '#0f172a'}
                                            >
                                                {blog.title}
                                            </h3>

                                            {/* Read More */}
                                            <div style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                color: '#2563eb', fontWeight: 700, fontSize: '0.88rem',
                                            }}>
                                                Read More
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                    <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        </Link>
                                    </motion.div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <ContactSection />
        </>
    );
}
